// External dependencies
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import cn from 'classnames';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

// Model types
import type { CreateStudyState } from '../../Create.model';
import type { StudyCode } from '@/model/StudyCode.model';

// Support modules
import { featuresSelector } from '@/support/Selectors.tsx';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';

// Internal components
import DatePickerNative from '@/components/UI/DatePickerNative';
import Select from '@/components/UI/Select/Select';

// Relative imports
import ProjectsField from './ProjectsField';
import { StudyCodeLookup } from './StudyCodeLookup';
import { trackingDayBaseOptions } from './Information.utils';

const informationFeatures = createSelector([featuresSelector], (features) => ({
  trackingDayBaseEnabled: features.tracking_day_base ?? false,
}));

interface InformationProps {
  state?: CreateStudyState;
  dispatch: (action: { type: string; data: unknown }) => void;
  settings?: boolean;
}

const Information: FC<InformationProps> = ({ state, dispatch, settings }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const { setStepReady } = useStudyCreateStep();
  const { trackingDayBaseEnabled } = useSelector(informationFeatures);

  useEffect(() => {
    setStepReady(true);
  }, [setStepReady]);

  const updateStudyCode = useCallback(
    (selectedStudyCode: StudyCode) => {
      dispatch({ type: 'updateForm', data: { selectedStudyCode } });
    },
    [dispatch]
  );

  const setProjectNameForSummary = useCallback(
    (selectedProjectName: string) => {
      dispatch({ type: 'updateForm', data: { selectedProjectName } });
    },
    [dispatch]
  );

  return (
    <div data-testid="information-settings__container">
      <div className="ui-card mw7">
        <div className="pa4">
          <div>
            <div className="mb3">
              <label htmlFor="name" data-testid="information-settings__study-name">
                Study name (required)
              </label>
              <input
                type="text"
                style={{ marginBottom: 0 }}
                className={cn({ input__error: errors.name })}
                autoFocus
                data-testid="study-name-field"
                autoComplete="off"
                {...register('name', {
                  minLength: {
                    value: 3,
                    message: 'Enter a study name that is longer than 3 characters',
                  },
                  maxLength: {
                    value: 255,
                    message: 'Study name must be less than 255 characters',
                  },
                  required: 'A study name is required',
                })}
              />
              <ErrorMessage
                errors={errors}
                name="name"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
            <div className="mb3">
              <label htmlFor="notes">Description</label>
              <textarea
                cols={30}
                rows={10}
                style={{ marginBottom: 0, resize: 'vertical' }}
                className={cn({ input__error: errors.notes })}
                data-testid="study-description-field"
                {...register('notes', {
                  maxLength: {
                    value: 2048,
                    message: 'Maximum of 2048 characters',
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="notes"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
            <div className="mb3">
              <StudyCodeLookup settings={settings} updateStudyCode={updateStudyCode} />
            </div>
            {trackingDayBaseEnabled && (
              <div className="mb3" data-testid="tracking-day-base-field">
                <label className="db">
                  Tracking day base
                  <p className="f6 mid-gray mb2 normal">Set which day number the animal’s tracking date represents.</p>
                  <Controller
                    name="tracking_day_base"
                    control={control}
                    defaultValue={state?.study?.tracking_day_base ?? 0}
                    render={({ field }) => (
                      <Select
                        value={trackingDayBaseOptions.find((o) => o.value === field.value) ?? trackingDayBaseOptions[1]}
                        options={trackingDayBaseOptions}
                        onChange={(option) => field.onChange(option?.value ?? 0)}
                        isMulti={false}
                      />
                    )}
                  />
                </label>
              </div>
            )}
            <div className="mb3">
              <ProjectsField setProjectNameForSummary={setProjectNameForSummary} />
            </div>
            <div>
              <label>Study start date</label>
              <Controller
                defaultValue={state?.study?.started_on}
                name="started_on"
                control={control}
                rules={{
                  required: 'This field is required',
                }}
                render={({ field }) => (
                  <DatePickerNative
                    value={field.value}
                    style={{ width: '260px', marginBottom: 0 }}
                    onChange={field.onChange}
                    testId="study-start-date-field"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Information;
